import type { MetaFunction } from "@remix-run/react"
import {
  articleBodyStyle,
  articleSection,
  bold,
  h1Style,
  h1Wrap,
  headerTextStyle,
  highlightHead,
  orderedList,
} from "./article.css"
import Footer from "~/components/Footer"
import { page } from "~/shared/styles.css"
import Logo from "~/components/Logo"
import { demoGrid, flipBgH2, gridH2Style, h2Style, masonryItem } from "./index.css"
import Link from "~/components/Link"
import StickyHeader from "~/components/StickyHeader"

const canUseDOM = !!(typeof window !== "undefined" && window?.matchMedia)

const exampleImages = [
  {
    url: "/how-to-use-ai-for-landscape-design/AI-landscape-visualization-rose-cottage-backyard.jpeg",
    alt: "A backyard that one of our users wants to re-design",
  },
  {
    url: "/how-to-use-ai-for-landscape-design/AI-landscape-visualization-rose-cottage-backyard-after.webp",
    alt: "A beautifully maintained country house garden with cottage style flowerbeds.",
  },
  {
    url: "/how-to-use-ai-for-landscape-design/AI-xeriscape-landscape-design.jpeg",
    alt: "An empty front garden, just grass and a driveway.",
  },
  {
    url: "/how-to-use-ai-for-landscape-design/AI-Xeriscape-landscape-design-after.webp",
    alt: "The AI for landscape design process we offer has added drought tolerant plants and suggested a decorative layout for the flowerbeds.",
  },
  {
    url: "/how-to-use-ai-for-landscape-design/AI-powered-yard-transformation.jpeg",
    alt: "An empty backyard.",
  },
  {
    url: "/how-to-use-ai-for-landscape-design/AI-powered-yard-transformation-(contemporary-pegola-with-a-relaxed-seating-area).webp",
    alt: "A wooden pergola, modern contemporary seating and a pleasing plant selection including a Crimson King Maple, all generated with AI for landscape design.",
  },
  {
    url: "/how-to-use-ai-for-landscape-design/AI-landscape-design-for-small-spaces.jpeg",
    alt: "Small space landscape design requirement..",
  },
  {
    url: "/how-to-use-ai-for-landscape-design/AI-landscape-design-for-small-spaces-after.webp",
    alt: "The design includes a wooden pergola framing the garden from the house side view, draped in beautiful climbing roses and more manicured lawn.",
  },
  {
    url: "/how-to-use-ai-for-landscape-design/AI-landscape-design-for-climate-adaptation.jpeg",
    alt: "Landscape design for climate adaptation",
  },
  {
    url: "/how-to-use-ai-for-landscape-design/AI-landscape-design-for-climate-adaptation-after.webp",
    alt: "Design a climate-resilient, wildlife-friendly garden with AI, featuring adaptive plants and a water feature to support local ecosystems.",
  },
  {
    url: "/how-to-use-ai-for-landscape-design/AI-landscape-design-for-urban-rooftops.webp",
    alt: "landscape design for urban rooftops",
  },
  {
    url: "/how-to-use-ai-for-landscape-design/AI-landscape-design-for-urban-rooftops-after.webp",
    alt: "Transform urban rooftops into green oases with AI-designed gardens, using roof-friendly plants to create a skyline sanctuary like this. Struggling for rooftop garden ideas? Try our AI powered visualisation tool.",
  },
  {
    url: "/how-to-use-ai-for-landscape-design/Eco-friendly-AI-landscape-solutions.jpeg",
    alt: "Eco-friendly landscape solutions",
  },
  {
    url: "/how-to-use-ai-for-landscape-design/Eco-friendly-AI-landscape-solutions-after-(tropical-exotic-flowering-plantings-and-orchids).webp",
    alt: "Create a sustainable tropical paradise with AI, showcasing exotic flowering plants and orchids within an environmentally conscious design.",
  },
  {
    url: "/how-to-use-ai-for-landscape-design/AI-sloped-backyard-design.webp",
    alt: "Landscape design for sloped backyard",
  },
  {
    url: "/how-to-use-ai-for-landscape-design/AI-sloped-backyard-design-(contemporary-raised-flower-beds-terraced).webp",
    alt: "Not sure what to do with your sloped backyard? Try our AI-designed contemporary gardens, featuring raised flower beds and terracing for both aesthetics and functionality. Never run out of ideas for your sloped backyard.",
  },
]

export type PreviewImage = {
  url: string
  size: { w: number; h: number }
}

export type Url = { url: string; initial?: PreviewImage | null; loading?: boolean }

export const meta: MetaFunction = () => {
  return [
    {
      title: "How To | AI Gardening With Rescape AI | Gardening Tutorial",
    },
    {
      name: "description",
      content:
        "Discover how Rescape AI excels at landscape design and can help generate professional-level backyard ideas.",
    },
    {
      property: "og:title",
      content: "How to use AI for landscape design with Rescape AI",
    },
    {
      property: "og:description",
      content:
        "Discover where AI landscape design from Rescape AI can help generate backyard design ideas.",
    },
    {
      property: "og:image",
      content: "https://rescapeai.com/articles/ai-yard-design-patio-and-flowerbeds.webp",
    },
    {
      tagName: "link",
      rel: "canonical",
      href: "https://rescapeai.com/article/how-to-use-ai-for-landscape-design",
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        name: "description",
        headline: "Landscape Design with AI - A How To Guide using Rescape AI",
        description:
          "Discover how Rescape AI excels at landscape design and can help generate professional-level backyard ideas.",
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2024-09-19",
        dateModified: "2024-09-19",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: "https://rescapeai.com/landing/modern-garden.webp",
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": "https://rescapeai.com/article/how-to-use-ai-for-landscape-design",
        },
        articleSection: [
          "Technology",
          "Landscape Design",
          "Artificial Intelligence",
          "Gardening",
        ],
      },
    },
  ]
}

function GridTitle({
  children,
  textAlign,
}: {
  children: string
  textAlign: "left" | "right" | "center"
}) {
  const align = {
    right: { alignItems: "flex-end" },
    left: { alignItems: "flex-start" },
    center: { alignItems: "center" },
  }
  return (
    <div
      className={masonryItem}
      style={{
        marginBottom: "0",
        display: "flex",
        justifyContent: "flex-end",
        flexDirection: "column",
        ...align[textAlign],
      }}
    >
      <h2
        className={`${gridH2Style} ${textAlign === "left" ? flipBgH2 : ""}`}
        style={{
          textAlign,
          marginTop: "0",
          height: "fit-content",
          ...(textAlign === "center"
            ? {
                fontSize: "1.6em",
                lineHeight: "1.2em",
              }
            : {}),
        }}
      >
        {children}
      </h2>
    </div>
  )
}

function Row({
  dir,
  title,
  images,
}: {
  dir: "right" | "left"
  title: string
  images: typeof exampleImages
}) {
  const isMob = canUseDOM ? window?.matchMedia("(max-width: 767px)")?.matches : null

  return (
    <>
      {(dir === "right" || isMob) && (
        <GridTitle textAlign={isMob ? "center" : dir}>{title}</GridTitle>
      )}
      {images.map(({ url, alt }) => (
        <img
          key={url}
          src={url}
          alt={alt}
          style={{ width: "100%", height: "auto", borderRadius: "6px" }}
          loading="lazy"
        />
      ))}
      {dir === "left" && !isMob && <GridTitle textAlign={dir}>{title}</GridTitle>}
    </>
  )
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage:
            "url(/how-to-use-ai-for-landscape-design/how-to-use-ai-cover.webp)",
          backgroundPosition: "50%",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>
            Using AI for Landscape Design with Rescape AI - step by step guide
          </h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />
      <div className={page}>
        <section className={articleSection}>
          <p className={articleBodyStyle}>
            AI landscape design brings to life what many of us dream about yet few realise
            for our own backyards. A well designed backyard generated with AI for us to
            enjoy. Spend countless hours in your garden oasis enjoying your beautifully
            landscaped backyard.
          </p>
          <p className={articleBodyStyle}>
            Crafting the right design and layout for your backyard is tricky and can be
            expensive without a clear idea of what you want; that is where AI landscape
            design from Rescape AI can help out. Our powerful generative AI models use
            images of your garden to rapidly generate ideas based on your real backyard,
            leading to a clearer direction for you to take to the real world.
          </p>
          <p className={articleBodyStyle}>
            Based on simple pre-defined prompts and images of your garden; you can use
            RescapeAI to re-imagine your backyard.{" "}
            <Link href="/article/transform-your-garden-with-rescape-ai#how-to">
              It's actually relatively simple
            </Link>
          </p>
          <section className={articleSection}>
            <h2
              id="how-to"
              className={`${h2Style} ${highlightHead}`}
              style={{ textAlign: "center" }}
            >
              Steps for Landscape Design with AI
            </h2>
            <ol className={orderedList} style={{ maxWidth: "728px", margin: "0 auto" }}>
              <li>
                <b className={bold}>
                  Photograph your garden when it is in the best light; try to avoid large
                  areas in shadow and glare from the sun.
                </b>
              </li>
              <li>
                The platform is designed to be <b className={bold}>user-friendly</b>,
                guiding you through each step.
              </li>
              <li>
                Choose a style and set of features - the AI will redesign your garden
                based on these choices
              </li>
              <li>
                <b className={bold}>Set Your Preferences:</b> Adjust the creativity level
                according to your preference.
              </li>
              <li>
                <b className={bold}>Generate Your Designs:</b> Press generate and you'll
                soon have design options to choose from.
              </li>
              <li>
                <b className={bold}>Refine:</b> Make any necessary tweaks, by remixing the
                image and download your favorite options.
              </li>
            </ol>
          </section>
        </section>
        <section className={articleSection} id="examples">
          <h2
            className={h2Style}
            style={{ lineHeight: "1em", maxWidth: "80%", margin: "0 auto 2em auto" }}
          >
            AI Landscape Design examples - transforming outdoor spaces
          </h2>
          <div className={demoGrid}>
            <Row
              dir="right"
              title="AI landscape visualization (rose, cottage, informal, diverse paints)"
              images={exampleImages.slice(0, 2)}
            />
            <Row
              dir="left"
              title="AI xeriscape landscape design (mediterranean, Drought-tolerant plant selections)"
              images={exampleImages.slice(2, 4)}
            />
            <Row
              dir="right"
              title="AI-powered yard transformation (contemporary, pergola with a relaxed seating area)"
              images={exampleImages.slice(4, 6)}
            />
            <Row
              dir="left"
              title="AI landscape design for small spaces (urban, wooden pergola)"
              images={exampleImages.slice(6, 8)}
            />
            <Row
              dir="right"
              title="AI landscape design for climate adaptation (wildlife-friendly garden, a water feature)"
              images={exampleImages.slice(8, 10)}
            />
            <Row
              dir="left"
              title="AI landscape design for urban rooftops (roof garden, roof-friendly plantings)"
              images={exampleImages.slice(10, 12)}
            />
            <Row
              dir="right"
              title="Eco-friendly AI landscape solutions (tropical, exotic flowering plantings and orchids)"
              images={exampleImages.slice(12, 14)}
            />
            <Row
              dir="left"
              title="AI sloped backyard design (contemporary, raised flower beds, terraced)"
              images={exampleImages.slice(14, 16)}
            />
          </div>
        </section>
        <section className={articleSection}>
          <p className={articleBodyStyle}>
            <Link href="/hello/signup">Create an account</Link> and purchase credits from
            $0.06 per image to generate designs like this today.
          </p>
        </section>
        <Footer />
      </div>
    </>
  )
}

export default Index
